import { Suspense, useState, useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchCurrentUser, api } from "../../../shared/api";
import { Navigate } from "react-router-dom";
import { BREAKPOINTS, MODULE_NAV, forbiddenHref, routeAccess, type ModuleNavItem } from "../../../shared/config";
import "./module-layout.css";

const COLLAPSE_KEY = "atino:sidebar-collapsed";
const GROUP_KEY_PREFIX = "atino:sidebar-groups:";
/** Thoi luong hieu ung dong/mo sidebar — PHAI khop `--ml-anim` trong module-layout.css. */
const ANIM_MS = 260;

/**
 * Khung mot module: sidebar rieng ben trai, noi dung tran het be ngang con lai.
 *
 * Giu dung thoi quen cua ban cu: sidebar thu gon duoc, co link "Quay lai Menu"
 * o tren cung, va muc dang xem duoc danh dau. Trang thai thu gon luu o
 * localStorage nen khong bi mo lai moi lan chuyen trang.
 *
 * Module nao khai `groups` (hien chi nhanhvn — sidebar dai, ban Django gap
 * thanh 5 nhom) thi render kieu accordion: moi nhom nho trang thai gap/mo
 * rieng theo module, va nhom chua trang dang xem luon tu mo du truoc do da
 * gap — khong bao gio giau mat trang hien tai. Go tim kiem thi bo qua nhom,
 * hien phang tat ca ket qua khop cho de quet (giu nguyen hanh vi cu). Module
 * khong khai `groups` thi hien danh sach phang nhu truoc gio, khong doi gi.
 *
 * PHAN QUYEN: khong co quyen vao module / muc dang xem (`routeAccess`) thi chuyen
 * sang trang /403 — ap cho moi trang dung khung nay. `checkItem={false}` cho trang
 * tu dieu huong theo quyen (vd trang chu Checklist dua nhan vien sang /worker).
 */
export function ModuleLayout({
  moduleKey,
  activeHref,
  checkItem = true,
  children,
}: {
  moduleKey: string;
  activeHref: string;
  checkItem?: boolean;
  children: ReactNode;
}) {
  const mod = MODULE_NAV[moduleKey];
  const [collapsed, setCollapsed] = useState(false);
  /**
   * Pha hieu ung. Trong luc chay, noi dung chinh chi TRUOT bang transform (GPU) va bo cuc
   * chi tinh lai MOT lan khi xong — truoc day transition `grid-template-columns` bat trinh
   * duyet dan trang lai ca bang lon moi khung hinh nen giat.
   */
  const [phase, setPhase] = useState<"idle" | "collapsing" | "expanding">("idle");
  const timer = useRef<number | null>(null);
  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navQuery, setNavQuery] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  // Doc localStorage sau khi mount, trong try/catch: che do rieng tu hoac
  // trinh duyet chan luu tru se nem ngay o buoc doc.
  useEffect(() => {
    try {
      setCollapsed(window.localStorage.getItem(COLLAPSE_KEY) === "1");
    } catch {
      /* khong luu duoc thi cu de mo */
    }
    try {
      const raw = window.localStorage.getItem(GROUP_KEY_PREFIX + moduleKey);
      setCollapsedGroups(
        raw ? (JSON.parse(raw) as Record<string, boolean>) : {},
      );
    } catch {
      setCollapsedGroups({});
    }
  }, [moduleKey]);

  useEffect(() => {
    setMobileOpen(false);
  }, [activeHref]);

  const toggle = () => {
    if (phase !== "idle") return;
    const next = !collapsed;
    try {
      window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
    } catch {
      /* khong luu duoc cung khong sao */
    }
    const khongHieuUng =
      window.innerWidth <= BREAKPOINTS.md ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (khongHieuUng) {
      setCollapsed(next);
      return;
    }
    if (next) {
      // Thu gon: sidebar hep dan + chu mo di, XONG moi doi sang bo cuc thu gon.
      setPhase("collapsing");
      timer.current = window.setTimeout(() => {
        setCollapsed(true);
        setPhase("idle");
      }, ANIM_MS);
    } else {
      // Mo rong: dung bo cuc day du ngay (bi cat), sidebar rong dan de lo ra.
      setCollapsed(false);
      setPhase("expanding");
      timer.current = window.setTimeout(() => setPhase("idle"), ANIM_MS);
    }
  };

  /** Gap/mo mot nhom trong sidebar — nho rieng theo tung module. */
  const toggleGroup = (key: string) => {
    setCollapsedGroups((cu) => {
      const next = { ...cu, [key]: !(cu[key] ?? true) };
      try {
        window.localStorage.setItem(
          GROUP_KEY_PREFIX + moduleKey,
          JSON.stringify(next),
        );
      } catch {
        /* khong luu duoc cung khong sao */
      }
      return next;
    });
  };

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchCurrentUser,
    retry: false,
  });

  const logout = async () => {
    await api.post("/auth/logout", {}).catch(() => undefined);
    window.location.href = "/login";
  };

  const q = navQuery.trim().toLocaleLowerCase("vi");
  const khop = (item: ModuleNavItem): boolean =>
    item.label.toLocaleLowerCase("vi").includes(q);
  const permissions = new Set(user?.permissions ?? []);
  const visibleItems = (mod?.items ?? []).filter(
    (item) =>
      !item.hidden &&
      (!item.permissions?.length ||
        item.permissions.some((permission) => permissions.has(permission))),
  );
  const itemByHref = new Map(
    visibleItems.map((item) => [item.href, item] as const),
  );
  // Sidebar da thu gon con icon thi bo nhom — tieu de nhom khong co icon,
  // gap vao che do do se roi thanh mot dong chu trong khong.
  const hienNhom = Boolean(mod?.groups) && !q && !collapsed;

  const renderLink = (item: ModuleNavItem) => {
    const active = item.href === activeHref;
    return (
      <a
        key={item.href}
        href={item.href}
        className={`ml-link${active ? " is-active" : ""}`}
        title={collapsed ? item.label : undefined}
      >
        <span className="ml-icon">{item.icon}</span>
        <span className="ml-label">{item.label}</span>
        {/* Nhan trang thai chi con dung cho Checklist duoc giu lai. */}
        {!item.migrated && <span className="ml-old ml-label">cũ</span>}
      </a>
    );
  };

  const access = user ? routeAccess(moduleKey, activeHref, user, checkItem) : null;
  if (access && !access.ok) return <Navigate to={forbiddenHref(access.feature)} replace />;

  return (
    <div
      className={`ml${collapsed ? " is-collapsed" : ""}${phase === "collapsing" ? " is-anim-collapse" : ""}${phase === "expanding" ? " is-anim-expand" : ""}${mobileOpen ? " is-mobile-open" : ""}`}
    >
      <header className="ml-mobile-bar">
        <button
          type="button"
          className="ml-mobile-menu"
          onClick={() => setMobileOpen(true)}
          aria-label="Mở điều hướng"
        >
          <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </button>
        <img className="ml-brand-mark" src="/brand/atino-mark.png" alt="Atino" />
        <strong>{mod?.name ?? moduleKey}</strong>
      </header>

      <button
        type="button"
        className="ml-scrim"
        onClick={() => setMobileOpen(false)}
        aria-label="Đóng điều hướng"
      />

      <aside className="ml-side">
        <div className="ml-side-top">
          <a className="ml-brand" href="/apps" title="Atino">
            {/* Logo chu "Atino"; thu gon thi cat dan con chu "A" (chu dau cua chinh logo). */}
            <img className="ml-brand-word" src="/brand/atino-logo.png" alt="Atino" />
          </a>
          <button
            type="button"
            className="ml-mobile-close"
            onClick={() => setMobileOpen(false)}
            aria-label="Đóng điều hướng"
          >
            ×
          </button>
        </div>

        <div className="ml-side-navtop">
          <a className="ml-back" href="/apps" title="Quay lại Menu">
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            <span className="ml-label">Tất cả ứng dụng</span>
          </a>
          <button
            type="button"
            className="ml-toggle"
            onClick={toggle}
            aria-label={collapsed ? "Mở rộng thanh bên" : "Thu gọn thanh bên"}
            title={collapsed ? "Mở rộng" : "Thu gọn"}
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={collapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"}
              />
            </svg>
          </button>
        </div>

        <div className="ml-module">
          <span className="ml-label">{mod?.name ?? moduleKey}</span>
          <span className="ml-count ml-label">{visibleItems.length}</span>
        </div>

        <label className="ml-search ml-label">
          <svg
            fill="none"
            stroke="currentColor"
            strokeWidth={1.7}
            viewBox="0 0 24 24"
          >
            <circle cx="11" cy="11" r="7" />
            <path strokeLinecap="round" d="m20 20-4-4" />
          </svg>
          <input
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            placeholder="Tìm chức năng…"
            aria-label="Tìm chức năng"
          />
        </label>

        <nav className="ml-nav">
          {hienNhom
            ? mod?.groups?.map((g) => {
                const muc = g.hrefs
                  .map((h) => itemByHref.get(h))
                  .filter((it): it is ModuleNavItem => Boolean(it));
                if (muc.length === 0) return null;
                const coDangXem = muc.some((it) => it.href === activeHref);
                // Dung y ban Django: mac dinh GAP tru khi nguoi dung da tung mo
                // (nho trong localStorage), va nhom chua trang dang xem LUON
                // mo — du truoc do da gap — de khong bao gio giau mat trang
                // hien tai. Viec force-mo nay KHONG ghi lai localStorage.
                const gap = coDangXem
                  ? false
                  : (collapsedGroups[g.key] ?? true);
                return (
                  <div
                    key={g.key}
                    className={`ml-grp${gap ? " is-collapsed" : ""}`}
                  >
                    <button
                      type="button"
                      className="ml-grp-head"
                      onClick={() => toggleGroup(g.key)}
                    >
                      <span className="ml-label">{g.label}</span>
                      <svg
                        className="ml-grp-car"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.2}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                    <div className="ml-grp-body">{muc.map(renderLink)}</div>
                  </div>
                );
              })
            : visibleItems.filter(khop).map(renderLink)}
          {navQuery && !visibleItems.some(khop) && (
            <p className="ml-empty ml-label">Không có chức năng phù hợp</p>
          )}
        </nav>

        <div className="ml-side-foot">
          <span className="ml-user ml-label">{user?.fullName ?? "…"}</span>
          {/* Nhu muc "Đổi mật khẩu" trong khoi Tài khoản cua sidebar Django (trang ho so). */}
          <a className="ml-logout ml-uiset" href="/ho-so" title="Hồ sơ & đổi mật khẩu">
            <svg fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
            </svg>
            <span className="ml-label">Đổi mật khẩu</span>
          </a>
          {/* Nhu muc "Cài đặt giao diện" trong khoi Tài khoản cua sidebar Django. */}
          <a
            className="ml-logout ml-uiset"
            href="/cai-dat/giao-dien"
            title="Cài đặt giao diện"
          >
            <svg fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="9" />
              <circle cx="8.5" cy="10" r="1.2" />
              <circle cx="12" cy="7.5" r="1.2" />
              <circle cx="15.5" cy="10" r="1.2" />
              <path strokeLinecap="round" d="M12 21a3 3 0 0 1 0-6h2a3 3 0 0 0 3-3" />
            </svg>
            <span className="ml-label">Cài đặt giao diện</span>
          </a>
          <button
            type="button"
            className="ml-logout"
            onClick={logout}
            title="Đăng xuất"
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span className="ml-label">Đăng xuất</span>
          </button>
        </div>
      </aside>

      <main className="ml-main">
        <Suspense fallback={<div className="ml-loading" role="status">Đang tải trang…</div>}>{userLoading ? null : children}</Suspense>
      </main>
    </div>
  );
}
